import React, { type ReactNode, useEffect, useMemo } from 'react';
import useCurrentUser from 'infrastructure/currentUser/useCurrentUser';
import { getCardPreviewBundledFonts } from 'slices/Cards/helpers/cardPreviewFonts';
import useGetUploadedFonts from 'slices/StudioAssets/Fonts/hooks/useGetUploadedFonts';
import {
  clearUploadedFontsStyleTagSource,
  syncUploadedFontsStyleTagSource,
  type UploadedFontCssSource,
} from './uploadedFonts';
import useCurrentTenant from './useCurrentTenant';

const CURRENT_TENANT_UPLOADED_FONTS_SOURCE = 'current-tenant-uploaded-fonts';

function CurrentTenantUploadedFontsProvider({
  children,
  loadCmsUploadedFonts = true,
  skipAuthRedirect = false,
  fallbackFonts,
}: {
  children: ReactNode;
  loadCmsUploadedFonts?: boolean;
  skipAuthRedirect?: boolean;
  fallbackFonts?: UploadedFontCssSource[] | null;
}) {
  const { currentTenant } = useCurrentTenant();
  const { currentUser, hasAnalyticsViewerPermissions } = useCurrentUser();
  const shouldLoadCmsUploadedFonts =
    loadCmsUploadedFonts &&
    Boolean(currentUser) &&
    !hasAnalyticsViewerPermissions;
  const bundledFonts = useMemo(
    () => getCardPreviewBundledFonts(currentTenant?.name),
    [currentTenant?.name]
  );
  const { data: cmsUploadedFonts } = useGetUploadedFonts({
    enabled: shouldLoadCmsUploadedFonts,
    skipAuthRedirect,
  });
  const fonts = shouldLoadCmsUploadedFonts ? cmsUploadedFonts : undefined;

  useEffect(() => {
    if (
      shouldLoadCmsUploadedFonts &&
      !fonts &&
      !fallbackFonts &&
      bundledFonts.length === 0
    ) {
      return undefined;
    }

    syncUploadedFontsStyleTagSource(
      CURRENT_TENANT_UPLOADED_FONTS_SOURCE,
      fonts,
      fallbackFonts,
      bundledFonts
    );

    return () =>
      clearUploadedFontsStyleTagSource(CURRENT_TENANT_UPLOADED_FONTS_SOURCE);
  }, [bundledFonts, fallbackFonts, fonts, shouldLoadCmsUploadedFonts]);

  return children as React.ReactElement;
}

export default CurrentTenantUploadedFontsProvider;
